import React, { useEffect, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Autocomplete, IconButton, Box, Typography,
    Select, MenuItem, InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

interface AssignActivityModalProps {
    open: boolean;
    onClose: () => void;
    employeeId?: number | null;
    onAssign?: (payload: any) => Promise<any> | void;
    categories: any[];
    technologies: any[];
    employees: any[];
    activities: any[];
}

const AssignActivityModal: React.FC<AssignActivityModalProps> = ({
    open, onClose, onAssign,
    categories, technologies, employees, activities
}) => {

    const [form, setForm] = useState<any>({
        activityId: 0,
        categoryId: 0,
        technologyId: 0,
        name: "",
        description: "",
        pointOfContactId: 0,
        hours: 0,
        status: "Yet to start"
    });

    const [isExistingActivity, setIsExistingActivity] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<any>({});
    const [snackbar, setSnackbar] = useState({ open: false, message: "" });

    useEffect(() => {
        if (!open) {
            setForm({
                activityId: 0,
                categoryId: 0,
                technologyId: 0,
                name: "",
                description: "",
                pointOfContactId: 0,
                hours: 0,
                status: "Yet to start"
            });
            setIsExistingActivity(false);
            setErrors({});
        }
    }, [open]);

    const handleChange = (field: string, value: any) => {
        setForm((prev: any) => ({ ...prev, [field]: value }));
    };

    const validate = () => {
        const e: any = {};
        if (!form.name?.trim()) e.name = "Activity required";
        else if (form.name.length > 80) e.name = "Please limit to 80 characters";
        else if (!/[a-zA-Z]/.test(form.name)) e.name = "Activity name cannot be only numbers or special characters";
        if (!isExistingActivity && !form.categoryId) e.category = "Category required";
        if (!isExistingActivity && !form.technologyId) e.technology = "Technology required";
        if (!isExistingActivity && !form.description?.trim()) e.description = "Description required";
        if (!form.pointOfContactId) e.poc = "Point of contact required";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const resetForm = () => {
        setForm({
            activityId: 0,
            categoryId: 0,
            technologyId: 0,
            name: "",
            description: "",
            pointOfContactId: 0,
            hours: 0,
            status: "Yet to start"
        });
        setIsExistingActivity(false);
        setErrors({});
        setSnackbar({ open: false, message: "" });
    };

    const handleAssign = async () => {
        if (!validate()) {
            setSnackbar({ open: true, message: "Please fill required fields" });
            return;
        }
        if (!isExistingActivity) {
            const normalize = (name: string) => (name || "").toLowerCase().replace(/[^a-z0-9]/gi, "");
            const inputNorm = normalize(form.name);
            const duplicate = (activities || []).some((a: any) => {
                const status = String(a.status || a.activityStatus || a.activityState || '').toLowerCase();
                if (status !== 'active') return false;
                const norm = normalize(a.activityName ?? a.name);
                return norm === inputNorm;
            });
            if (duplicate) {
                setSnackbar({ open: true, message: "An activity with this name already exists." });
                return;
            }
        }
        try {
            setSaving(true);

            const result = await onAssign?.({
                activityId: form.activityId,
                name: form.name,
                categoryId: form.categoryId,
                technologyId: form.technologyId,
                description: form.description,
                pointOfContactId: form.pointOfContactId,
                status: "Yet to start",
                isNew: true
            });

            if (result && result.duplicate) {
                setSnackbar({ open: true, message: result.message || 'Duplicate assignment' });
                return;
            }

            onClose();
        } catch {
            setSnackbar({ open: true, message: "Error saving activity" });
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (open) resetForm();
    }, [open]);

    const normalize = (name: string) => (name || "").toLowerCase().replace(/[^a-z0-9]/gi, "");
    const seen = new Set<string>();
    const activityOptions = (activities || [])
        .filter((a: any) => {
            const status = String(a.status || a.activityStatus || a.activityState || '').toLowerCase();
            return status === 'active';
        })
        .map((a: any) => ({
            activityId: a.activityId ?? a.id,
            activityName: a.activityName ?? a.name,
            activityDescription: a.activityDescription ?? a.description,
            categoryId: a.categoryId,
            technologyId: a.technologyId,
            pointOfContactId: a.pointOfContactId
        }))
        .filter((a: any) => {
            const norm = normalize(a.activityName);
            if (seen.has(norm)) return false;
            seen.add(norm);
            return true;
        });

    const sortedActivityOptions = activityOptions.slice().sort((x: any, y: any) =>
        String(x.activityName || '').trim().toLowerCase().localeCompare(String(y.activityName || '').trim().toLowerCase())
    );

    /* ── Compact shared style tokens ── */
    const labelStyle: React.CSSProperties = {
        fontSize: '12px',
        fontWeight: 500,
        color: '#1a1a1a',
        marginBottom: '4px',
        display: 'block',
    };
    const requiredStar: React.CSSProperties = { color: '#d32f2f', marginLeft: '2px' };
    const fieldWrap: React.CSSProperties = { marginBottom: '10px' };
    const inputSx = {
        borderRadius: '6px',
        backgroundColor: '#fff',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0d5dd' },
        '& .MuiInputBase-root': { fontSize: '12px' },
        '& .MuiInputBase-input': { padding: '6px 8px' },
    };

    // Only close modal on X or Cancel, not on backdrop or escape
    const handleDialogClose = (event: object, reason: string) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
        }
        resetForm();
        onClose();
    };
    return (
        <Dialog
            open={open}
            onClose={handleDialogClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: '12px' } }}
            disableEscapeKeyDown
        >
            {/* ── Header ── */}
            <DialogTitle sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                borderBottom: '1px solid #e0e0e0', py: 1.5, px: 3,
            }}>
                <Typography fontWeight={700} fontSize={16} sx={{ color: '#124B84' }}>
                    Assign Activity
                </Typography>
                <IconButton size="small" onClick={onClose} sx={{ color: '#124B84' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            {/* ── Form ── */}
            <DialogContent sx={{ px: 3, pt: 1.5, pb: 0 }}>

                {/* 1. Name */}
                <div style={fieldWrap}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                        <label style={{ ...labelStyle, marginBottom: 0 }}>
                            Name<span style={requiredStar}>*</span>
                        </label>
                    </div>
                    <Autocomplete
                        freeSolo
                        options={sortedActivityOptions}
                        getOptionLabel={(o: any) => typeof o === "string" ? o : o?.activityName || ""}
                        value={
                            isExistingActivity
                                ? activityOptions.find(a => a.activityId === form.activityId) || null
                                : form.name
                        }
                        onChange={(_, value: any) => {
                            if (!value) {
                                setIsExistingActivity(false);
                                setForm({ activityId: 0, name: "", categoryId: 0, technologyId: 0, description: "", pointOfContactId: 0, status: "Active" });
                                setErrors({});
                                return;
                            }
                            if (typeof value === "string") {
                                setIsExistingActivity(false);
                                setForm({ activityId: 0, name: value.slice(0, 50), categoryId: 0, technologyId: 0, description: "", pointOfContactId: 0, status: "Active" });
                                setErrors({});
                                return;
                            }
                            setIsExistingActivity(true);
                            setForm({
                                activityId: value.activityId,
                                name: (value.activityName || '').slice(0, 50),
                                description: value.activityDescription,
                                categoryId: value.categoryId,
                                technologyId: value.technologyId,
                                pointOfContactId: value.pointOfContactId || 0,
                                status: "Active"
                            });
                            setErrors({});
                        }}
                        onInputChange={(_, value, reason) => {
                            if (reason === "input") {
                                setIsExistingActivity(false);
                                setForm({ activityId: 0, name: value.slice(0, 50), categoryId: 0, technologyId: 0, description: "", pointOfContactId: 0, hours: 0, status: "Active" });
                                setErrors({});
                            }
                            if (value === "") {
                                setForm({ activityId: 0, name: "", categoryId: 0, technologyId: 0, description: "", pointOfContactId: 0, hours: 0, status: "Active" });
                                setErrors({});
                            }
                        }}
                        size="small"
                        renderInput={(params) => (
                            <>
                                <TextField
                                    {...params}
                                    placeholder="Activity Name"
                                    error={!!errors.name}
                                    helperText={errors.name}
                                    inputProps={{ ...params.inputProps, maxLength: 50 }}
                                    sx={inputSx}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px', mt: 0.5, ml: 0 }}>
                                    {form.name.length}/50 characters
                                </Typography>
                            </>
                        )}
                    />
                </div>

                {/* 2. Category */}
                <div style={fieldWrap}>
                    <label style={labelStyle}>
                        Category<span style={requiredStar}>*</span>
                    </label>
                    <Autocomplete
                        options={categories
                            ? categories.slice().sort((a: any, b: any) =>
                                String(a.name || '').trim().toLowerCase().localeCompare(String(b.name || '').trim().toLowerCase()))
                            : []}
                        disabled={isExistingActivity}
                        value={categories.find(c => c.id === form.categoryId) || null}
                        getOptionLabel={(o: any) => o.name || ""}
                        onChange={(_, v: any) => handleChange("categoryId", v?.id || 0)}
                        size="small"
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select"
                                error={!!errors.category}
                                helperText={errors.category}
                                sx={inputSx}
                            />
                        )}
                    />
                </div>

                {/* 3. Technology */}
                <div style={fieldWrap}>
                    <label style={labelStyle}>
                        Technology<span style={requiredStar}>*</span>
                    </label>
                    <Autocomplete
                        options={technologies
                            ? technologies.slice().sort((a: any, b: any) =>
                                String(a.name || '').trim().toLowerCase().localeCompare(String(b.name || '').trim().toLowerCase()))
                            : []}
                        disabled={isExistingActivity}
                        value={form.technologyId ? technologies.find(t => t.id === form.technologyId) || null : null}
                        getOptionLabel={(o: any) => o?.name || ""}
                        onChange={(_, v: any) => setForm((prev: any) => ({ ...prev, technologyId: v?.id || 0 }))}
                        size="small"
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select"
                                error={!!errors.technology}
                                helperText={errors.technology}
                                sx={inputSx}
                            />
                        )}
                    />
                </div>

                {/* 4. Description */}
                <div style={fieldWrap}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                        <label style={labelStyle}>
                            Description<span style={requiredStar}>*</span>
                        </label>
                    </div>
                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Write here..."
                        size="small"
                        disabled={isExistingActivity}
                        value={form.description}
                        onChange={(e) => {
                            const value = e.target.value.slice(0, 300);
                            handleChange("description", value);
                        }}
                        error={!!errors.description}
                        helperText={errors.description}
                        sx={inputSx}
                        inputProps={{ maxLength: 300 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px', mt: 0.5, ml: 0 }}>
                        {form.description.length}/300 characters
                    </Typography>
                </div>

                {/* 5. Point of Contact */}
                <div style={fieldWrap}>
                    <label style={labelStyle}>
                        Point of Contact<span style={requiredStar}>*</span>
                    </label>
                    <Autocomplete
                        options={employees
                            ? employees.slice().sort((a: any, b: any) => {
                                const na = String(a.displayName || a.name || a.empName || '').trim().toLowerCase();
                                const nb = String(b.displayName || b.name || b.empName || '').trim().toLowerCase();
                                return na.localeCompare(nb);
                            })
                            : []}
                        disabled={isExistingActivity}
                        value={
                            form.pointOfContactId
                                ? employees.find(e =>
                                    e.empCode === form.pointOfContactId ||
                                    e.employeeId === form.pointOfContactId ||
                                    e.id === form.pointOfContactId
                                ) || null
                                : null
                        }
                        getOptionLabel={(o: any) => o?.displayName || o?.name || ""}
                        isOptionEqualToValue={(option: any, value: any) =>
                            (option.empCode || option.employeeId || option.id) ===
                            (value.empCode || value.employeeId || value.id)
                        }
                        onChange={(_, v: any) =>
                            setForm((prev: any) => ({
                                ...prev,
                                pointOfContactId: v?.empCode || v?.employeeId || v?.id || 0
                            }))
                        }
                        size="small"
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select"
                                error={!!errors.poc}
                                helperText={errors.poc}
                                sx={inputSx}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {params.InputProps.endAdornment}
                                            <InputAdornment position="end">
                                                <SearchIcon sx={{ fontSize: 18, color: '#9e9e9e' }} />
                                            </InputAdornment>
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </div>

                {/* 6. Status */}
                <div style={{ ...fieldWrap, marginBottom: 0 }}>
                    <label style={labelStyle}>Status</label>
                    <Select
                        fullWidth
                        displayEmpty
                        value={form.status || "Yet to start"}
                        size="small"
                        disabled
                        sx={inputSx}
                        renderValue={(val) => val || <span style={{ color: '#9e9e9e', fontSize: '8px' }}>Yet to start</span>}
                    >
                        <MenuItem value="Yet to start">Yet to start</MenuItem>
                    </Select>
                </div>

            </DialogContent>

            {/* ── Footer Buttons ── */}
            <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1.5, justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    onClick={handleAssign}
                    disabled={saving}
                    sx={{
                        backgroundColor: '#124B84',
                        color: '#fff',
                        borderRadius: '6px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        '&:hover': { backgroundColor: '#0f3f70' },
                        '&:disabled': { backgroundColor: '#bdbdbd' },
                    }}
                >
                    {saving ? "Saving..." : isExistingActivity ? "Assign" : "Assign"}
                </Button>
                <Button
                    variant="contained"
                    onClick={() => { resetForm(); onClose(); }}
                    sx={{
                        backgroundColor: '#e53935',
                        color: '#fff',
                        borderRadius: '6px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        '&:hover': { backgroundColor: '#c62828' },
                    }}
                >
                    Cancel
                </Button>
            </DialogActions>

            {/* Snackbar */}
            {snackbar.open && (
                <Box sx={{
                    position: "fixed", bottom: 20, left: "50%",
                    transform: "translateX(-50%)",
                    bgcolor: "#d32f2f", color: "#fff",
                    px: 3, py: 1, borderRadius: 2, zIndex: 9999
                }}>
                    <span className="app-snackbar-message">{snackbar.message}</span>
                </Box>
            )}
        </Dialog>
    );
};

export default AssignActivityModal;